"use client";
import { Room } from "livekit-client";
import { ChatType } from "@shared/index";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { generateShortId } from "@client/index";
import { ChatMessage } from "@client/types/meet.types";
import { toastNotify } from "@client/components/ui/toast";
import { useAppSelector } from "@client/store/store.hooks";
import { DataMessage, FileChunkMessage } from "./useDataChannelManager";
import { saveChatMessage, getChatHistory } from "@client/actions/chat/chat.actions";
import { mirrorGuestChatActivity } from "@client/utils/guestChatMirror.client";
import { useState, useEffect, useCallback, useRef, RefObject, FormEvent, ChangeEvent, useMemo } from "react";

interface UseChatOptions {
  room: Room;
  showChat: boolean;
}

interface UseChatReturn {
  chatMessages: ChatMessage[];
  chatInput: string;
  unreadMessages: number;
  fileInputRef: RefObject<HTMLInputElement | null>;
  setChatInput: (input: string) => void;
  handleSendMessage: (e: FormEvent) => void;
  handleFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  addChatMessage: (message: ChatMessage) => void;
  resetUnreadMessages: () => void;
  incrementUnreadMessages: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const useChat = ({ room, showChat }: UseChatOptions): UseChatReturn => {
  const t = useTranslations();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use a ref for the room to avoid dependency issues in callbacks while staying updated
  const roomRef = useRef<Room>(room);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  // Reset unread messages when the chat sidebar is opened
  useEffect(() => {
    if (showChat) {
      setUnreadMessages(0);
    }
  }, [showChat]);

  // Redux Selectors
  const {
    roomId,
    joinUserName: localParticipantName,
    userId,
    hostId,
    isGuest,
    guestId,
    roomName
  } = useAppSelector((state) => ({
    roomId: state.connection.connectionDetails?.roomId,
    joinUserName: state.connection.connectionDetails?.joinUserName,
    userId: state.connection.connectionDetails?.userId || 0,
    hostId: state.connection.connectionDetails?.hostId || 0,
    isGuest: state.connection.connectionDetails?.isGuest || false,
    guestId: state.connection.connectionDetails?.guestId,
    roomName: state.connection.connectionDetails?.roomName
  }));

  const addChatMessage = useCallback(
    (message: ChatMessage) => {
      setChatMessages((prev) => {
        // Prevent duplicate messages by checking ID
        if (prev.some((msg) => msg.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });

      if (!showChat) {
        setUnreadMessages((prev) => prev + 1);
      }
    },
    [showChat]
  );

  const saveMessageToDb = useCallback(
    async (message: ChatMessage) => {
      if (!roomId) return;
      try {
        const messageToSave = {
          id: message.id,
          senderId: message.senderId,
          senderName: message.senderName,
          text: message.text,
          type: message.type,
          timestamp: message.timestamp,
          file: message.file
            ? {
                name: message.file.name,
                size: message.file.size,
                type: message.file.type
              }
            : undefined
        };

        const result = await saveChatMessage({ roomId, hostId, messages: messageToSave });

        if (!result.status) {
          Logger.error("useChat", "Failed to save message to DB:", result.message);
        }
      } catch (error) {
        Logger.error("useChat", "Error saving message to DB:", error);
      }
    },
    [roomId, hostId]
  );

  const publishData = useCallback((data: DataMessage) => {
    const currentRoom = roomRef.current;
    if (!currentRoom) return;

    const strData = JSON.stringify(data);
    const encoder = new TextEncoder();
    currentRoom.localParticipant.publishData(encoder.encode(strData), { reliable: true });
  }, []);

  // Load chat history on mount or when roomId changes
  useEffect(() => {
    if (!roomId) return;

    let isMounted = true;
    getChatHistory(roomId)
      .then((result) => {
        if (isMounted && result.status && result.messages) {
          setChatMessages(result.messages);
        }
      })
      .catch((err) => Logger.error("useChat", "Failed to load chat history:", err));

    return () => {
      isMounted = false;
    };
  }, [roomId]);

  const handleSendMessage = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmedInput = chatInput.trim();
      if (!trimmedInput) return;

      const senderName = localParticipantName || roomRef.current.localParticipant.name || t("participant.you");
      const timestamp = Date.now();
      const messageId = generateShortId();

      const message: ChatMessage = {
        id: messageId,
        senderId: userId,
        senderName,
        text: trimmedInput,
        type: ChatType.TEXT,
        timestamp
      };

      publishData({ type: "chat", id: messageId, text: trimmedInput, senderId: userId });
      saveMessageToDb(message);
      if (isGuest && guestId && roomName) {
        void mirrorGuestChatActivity({
          roomName,
          guestId,
          guestName: senderName,
          action: "message",
          text: trimmedInput,
          sentAt: new Date(timestamp).toISOString()
        });
      }
      addChatMessage(message);
      setChatInput("");
    },
    [
      chatInput,
      localParticipantName,
      userId,
      isGuest,
      guestId,
      roomName,
      publishData,
      saveMessageToDb,
      addChatMessage,
      t
    ]
  );

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        toastNotify.error(t("validation.fileTooLarge"));
        return;
      }

      const reader = new FileReader();
      reader.onload = async (evt) => {
        const dataUrl = evt.target?.result as string;
        const senderName = localParticipantName || roomRef.current.localParticipant.name || t("participant.you");
        const timestamp = Date.now();
        const fileText = `${file.name}`;
        const messageId = generateShortId();

        const fileData = {
          name: file.name,
          size: file.size,
          type: file.type,
          data: dataUrl
        };

        const message: ChatMessage = {
          id: messageId,
          senderId: userId,
          senderName,
          text: fileText,
          type: ChatType.FILE,
          timestamp,
          file: fileData
        };

        // Send file in chunks to avoid data channel limits
        const CHUNK_SIZE = 32 * 1024; // 32KB
        const totalChunks = Math.ceil(dataUrl.length / CHUNK_SIZE);
        const fileId = generateShortId();

        for (let i = 0; i < totalChunks; i++) {
          const chunk = dataUrl.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          publishData({
            type: "file-chunk",
            id: messageId,
            fileId,
            chunkIndex: i,
            totalChunks,
            chunk,
            metadata: {
              name: file.name,
              size: file.size,
              type: file.type
            },
            senderId: userId
          } as FileChunkMessage);
        }

        saveMessageToDb(message);
        addChatMessage(message);
      };
      reader.readAsDataURL(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [localParticipantName, userId, publishData, saveMessageToDb, addChatMessage, t]
  );

  const resetUnreadMessages = useCallback(() => setUnreadMessages(0), []);
  const incrementUnreadMessages = useCallback(() => setUnreadMessages((prev) => prev + 1), []);

  return useMemo(
    () => ({
      chatMessages,
      chatInput,
      unreadMessages,
      fileInputRef,
      setChatInput,
      handleSendMessage,
      handleFileSelect,
      addChatMessage,
      resetUnreadMessages,
      incrementUnreadMessages
    }),
    [
      chatMessages,
      chatInput,
      unreadMessages,
      handleSendMessage,
      handleFileSelect,
      addChatMessage,
      resetUnreadMessages,
      incrementUnreadMessages
    ]
  );
};
